import { useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { ApiOperations } from "~enums/apiOperations";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useNotification } from "~hooks/useNotification.ts";
import { useExportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useExportFixtures.ts";
import { useImportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useImportFixtures.tsx";
import type {
  FixtureEntityUpsert,
  FixturePartialUpsert,
  FixturePersonUpsert,
  FixtureTransformed,
  FixtureUpsert,
} from "~pages/Fixtures/types";
import { transformFixtureListResponse } from "~pages/Fixtures/utilities";
import type { FixtureResponse } from "~types/fixture.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateFixture } from "./useCreateFixture";
import { useDeleteFixture } from "./useDeleteFixture";
import { getFixtureListQueryKey } from "./useFetchAllSeasonFixtures";
import { getGetFixtureQueryKey } from "./useGetFixture";
import { useLockUnlockFixture } from "./useLockUnlockFixture";
import { useRecalculateSeries } from "./useRecalculateSeries";
import { useResetFixture } from "./useResetFixture";
import { useUpdateFixture } from "./useUpdateFixture";
import { useUpdateFixtureEntity } from "./useUpdateFixtureEntity";
import { useUpdateFixturePerson } from "./useUpdateFixturePerson";
import { useUpdateListFixture } from "./useUpdateListFixture";

type FixtureAdvancedUpsert = {
  fixture: FixtureUpsert;
  competitors: FixtureEntityUpsert[];
  participants: FixturePersonUpsert[];
  oldSeriesCode?: string;
};

type LockUnlockFixture = {
  fixtureId: string;
  locked: boolean;
};

type AffectedSeries = {
  seriesCode?: string | null;
  oldSeriesCode?: string | null;
};

export const useFixtureService = () => {
  const { mutateAsync: createFixtureFn, isPending: isCreatePending } = useCreateFixture();
  const { mutateAsync: updateFixtureFn, isPending: isUpdatePending } = useUpdateListFixture();
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteFixture();
  const { mutateAsync: lockUnlockFn, isPending: isLockUnlockPending } = useLockUnlockFixture();
  const { mutateAsync: resetFn, isPending: isResetPending } = useResetFixture();

  const { mutateAsync: updateDetailsFn, isPending: isDetailsUpdatePending } = useUpdateFixture();
  const { mutateAsync: updateEntityFn, isPending: isEntityUpdatePending } = useUpdateFixtureEntity();
  const { mutateAsync: updatePersonFn, isPending: isPersonUpdatePending } = useUpdateFixturePerson();
  const { mutate: recalculateSeries } = useRecalculateSeries();
  const { mutateAsync: importFixturesFn, isPending: isImportPending } = useImportFixtures();
  const { mutateAsync: exportFixturesFn, isPending: isExportPending } = useExportFixtures();

  const { hasPermission } = useUserPermissions();
  const { sport = "", organizationId = "", fixtureId = "", seasonId = "" } = useParams();
  const queryClient = useQueryClient();

  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { execute, create, update, remove, notifySuccess } = useApiOperations(ApiOperationItems.FIXTURE);

  const recalculateAffectedSeries = ({ seriesCode, oldSeriesCode }: AffectedSeries) => {
    if (oldSeriesCode && oldSeriesCode !== seriesCode) {
      recalculateSeries(oldSeriesCode);
    }

    if (seriesCode) {
      recalculateSeries(seriesCode);
    }
  };

  const createFixture = async (payload: FixturePartialUpsert) => {
    const isCreated = Boolean(await create(() => createFixtureFn(payload)));

    if (isCreated) {
      recalculateAffectedSeries({ seriesCode: payload.upsertData?.seriesCode });
    }

    return isCreated;
  };

  const updateFixture = async (payload: FixturePartialUpsert) => {
    const isUpdated = Boolean(await update(() => updateFixtureFn(payload)));

    if (isUpdated) {
      recalculateAffectedSeries({ seriesCode: payload.upsertData?.seriesCode, oldSeriesCode: payload.oldSeriesCode });
    }

    return isUpdated;
  };

  const updateFixtureAdvanced = async ({ fixture, competitors, participants, oldSeriesCode }: FixtureAdvancedUpsert) => {
    const editFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
    const editParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.UPDATE);
    const editCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.UPDATE);

    const results = await Promise.allSettled([
      ...(editFixtureAllowed ? [updateDetailsFn(fixture)] : []),
      ...(editParticipantsAllowed ? participants.map((payload) => updatePersonFn(payload)) : []),
      ...(editCompetitorsAllowed ? competitors.map((payload) => updateEntityFn(payload)) : []),
    ]);

    // The fixture details request is always the first one, but it is only sent when editing the fixture itself is allowed
    const detailsResult = editFixtureAllowed ? results[0] : undefined;

    if (detailsResult?.status === "fulfilled") {
      if (fixture.seriesCode !== oldSeriesCode) {
        recalculateAffectedSeries({ seriesCode: fixture.seriesCode, oldSeriesCode });
      }

      const updatedFixture: FixtureTransformed | undefined = transformFixtureListResponse(detailsResult.value as FixtureResponse)?.[0];

      if (updatedFixture) {
        queryClient.setQueryData(getFixtureListQueryKey({ sport, organizationId, seasonId }), (oldFixtures?: FixtureTransformed[]) =>
          oldFixtures?.map((oldFixture) => (oldFixture.fixtureId === fixtureId ? updatedFixture : oldFixture)),
        );
      }
    }

    queryClient.invalidateQueries({ queryKey: getGetFixtureQueryKey({ sport, organizationId, fixtureId, seasonId }) });

    // Failed requests are reported by the useApi error handlers
    if (results.every(({ status }) => status === "rejected")) {
      return false;
    }

    if (results.some(({ status }) => status === "rejected")) {
      notification.warning({ message: formatMessage({ id: "form.submission.partialSuccess" }) });
      return false;
    }

    notifySuccess(ApiOperations.UPDATE);

    return true;
  };

  const deleteFixture = async (fixtureIdToDelete: string) => Boolean(await remove(() => deleteFn(fixtureIdToDelete)));

  const lockUnlockFixture = async ({ fixtureId: fixtureIdToLockUnlock, locked }: LockUnlockFixture) =>
    Boolean(
      await execute(() => lockUnlockFn({ fixtureId: fixtureIdToLockUnlock, locked }), {
        successMessageId: locked ? "fixture.unlock.success" : "fixture.lock.success",
      }),
    );

  const resetFixture = async (fixtureIdToReset: string) =>
    Boolean(await execute(() => resetFn(fixtureIdToReset), { successMessageId: "fixture.clear.success" }));

  const importFixtures = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importFixturesFn(fileName), {
        successMessageId: "fixtures.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Fixtures import was not started",
    );

  const exportFixtures = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportFixturesFn(fieldNames), {
        successMessageId: "fixtures.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Fixtures export was not started",
    );

  return {
    createFixture,
    isCreatePending,
    updateFixture,
    isUpdatePending,
    updateFixtureAdvanced,
    isAdvancedUpdatePending: isDetailsUpdatePending || isEntityUpdatePending || isPersonUpdatePending,
    deleteFixture,
    isDeletePending,
    lockUnlockFixture,
    isLockUnlockPending,
    resetFixture,
    isResetPending,
    importFixtures,
    isImportPending,
    exportFixtures,
    isExportPending,
  };
};
